import { computed, ref } from 'vue'

import { withTimeout } from '@/composables/withTimeout'

export const SETUP_STATE_ENDPOINT = 'setup_state'
export const SETUP_REQUEST_TIMEOUT_MS = 15000

export const STORE_DETAILS_STEP = 'store_details'
export const PAYMENTS_STEP = 'payments'
export const CUSTOMERS_STEP = 'customers'
export const ASSISTANT_STEP = 'assistant'

// This list, not the payload, is what every surface counts off: a step the server
// stops sending is backfilled here as permanently incomplete, so dropping one has
// to happen on both sides or every merchant sits one circle short forever.
// Customers is held out on both sides until legal signs off on merchants
// importing their own contacts.
export const SETUP_STEP_KEYS = [STORE_DETAILS_STEP, PAYMENTS_STEP, ASSISTANT_STEP]

export const STRIPE_NOT_CONNECTED = 'not_connected'
export const STRIPE_PENDING = 'pending'
export const STRIPE_ACTION_REQUIRED = 'action_required'
export const STRIPE_CONNECTED = 'connected'

export const ECOMMERCE_PLATFORM = 'ecommerce'
export const CATERING_PLATFORM = 'catering'

// The assistant step points at the bot widget and asks for nothing else, so where
// no widget is mounted it is not a step: its copy names a button that is not on
// screen and its own button opens nothing. App.vue owns the same signal the widget
// mounts on and narrows this to match. The drawer, the badge, the banner and the
// orders empty state all count off this list, so dropping the step anywhere else
// would leave them disagreeing about what is left.
const assistantAvailable = ref(true)

export function setAssistantAvailable(available) {
  assistantAvailable.value = Boolean(available)
}

export function setupStepKeys() {
  if (assistantAvailable.value) return SETUP_STEP_KEYS
  return SETUP_STEP_KEYS.filter(key => key !== ASSISTANT_STEP)
}

function statusOf(label, tone) {
  return { label, tone }
}

function customersStatus(complete, data) {
  const count = Number(data.count)
  if (Number.isFinite(count) && count > 0) return statusOf(`${count} imported`, complete ? 'done' : 'todo')
  if (complete) return statusOf('Imported', 'done')
  return statusOf('None imported', 'todo')
}

// An explicit status outranks the step's own flag: a merchant sitting in Stripe
// verification must not be told they are connected by a stale complete. Stripe
// waiting on the merchant is its own state rather than a shade of pending: pending
// clears itself and this one waits for the merchant forever.
function paymentsStatus(complete, data) {
  if (data.status === STRIPE_ACTION_REQUIRED) return statusOf('Needs you', 'attention')
  if (data.status === STRIPE_PENDING) return statusOf('Pending verification', 'pending')
  if (data.status === STRIPE_NOT_CONNECTED) return statusOf('Not connected', 'todo')
  if (data.status === STRIPE_CONNECTED || complete) return statusOf('Connected', 'done')
  return statusOf('Not connected', 'todo')
}

// Saved would name the wrong thing now the step turns on the merchant confirming
// it: the name a merchant has not looked at yet is saved too, so saved is true of
// both halves of the badge and tells them nothing.
const STEP_STATUS = {
  [STORE_DETAILS_STEP]: complete => (complete ? statusOf('Confirmed', 'done') : statusOf('Needs review', 'todo')),
  [PAYMENTS_STEP]: paymentsStatus,
  [CUSTOMERS_STEP]: customersStatus,
  [ASSISTANT_STEP]: complete => (complete ? statusOf('Done', 'done') : statusOf('Not seen yet', 'todo'))
}

function stepData(step) {
  const data = step && step.data
  return data && typeof data === 'object' && !Array.isArray(data) ? data : {}
}

// The copy is what stops a form v-modelled onto a step writing straight through
// into the shared state, where a save that never landed would still move the
// badge and survive a cancel.
function normalizeStep(key, step) {
  return { key, complete: Boolean(step && step.complete), data: { ...stepData(step) } }
}

export function isSetupApplicable(state) {
  return Boolean(state && state.applicable)
}

// Gated on applicability with everything else: a merchant Bottle staff set up by
// hand must read as nothing to render on every accessor, not only on the list.
export function setupPlatform(state) {
  return (isSetupApplicable(state) && state.platform) || null
}

// Keyed on the canonical list rather than on what arrived, so a payload that
// omits, duplicates or reorders a step can neither reorder the drawer nor drop a
// step out of it. A step we were sent nothing for renders empty and asks.
export function setupSteps(state) {
  if (!isSetupApplicable(state)) return []
  const supplied = Array.isArray(state.steps) ? state.steps : []
  return setupStepKeys().map(key =>
    normalizeStep(
      key,
      supplied.find(step => step && step.key === key)
    )
  )
}

export function setupStep(state, key) {
  return setupSteps(state).find(step => step.key === key) || null
}

export function isStepComplete(state, key) {
  const step = setupStep(state, key)
  return Boolean(step && step.complete)
}

export function outstandingStepCount(state) {
  return setupSteps(state).filter(step => !step.complete).length
}

export function isSetupComplete(state) {
  return isSetupApplicable(state) && outstandingStepCount(state) === 0
}

// Read off the server's stamps rather than recomputed here. isSetupComplete is a
// live count and the payments step reads the Stripe columns, so a revoked account
// drops a merchant back to one step short: driving the review surfaces off the
// count would flicker them back into the step list.
export function isSetupPendingReview(state) {
  return isSetupApplicable(state) && Boolean(state.pending_review)
}

export function isSetupReviewed(state) {
  return isSetupApplicable(state) && Boolean(state.reviewed)
}

// applicable answers "is this a self-serve merchant with a drawer", and that
// stays true for the life of the account. Whether the drawer still belongs on
// screen is a different question with an end to it: everything these surfaces
// say exists to get the merchant to a store that trades, and once someone at
// Bottle has signed it off there is nothing left to tell them. The dashboard
// goes back to being a dashboard.
export function isSetupVisible(state) {
  return isSetupApplicable(state) && !isSetupReviewed(state)
}

export function firstIncompleteStepKey(state) {
  const step = setupSteps(state).find(candidate => !candidate.complete)
  return step ? step.key : null
}

export function setupProgress(state) {
  const steps = setupSteps(state)
  const completed = steps.filter(step => step.complete).length
  const total = steps.length
  return {
    completed,
    total,
    percent: total ? Math.round((completed / total) * 100) : 0,
    label: `${completed} / ${total}`
  }
}

export function stepStatus(step) {
  const complete = Boolean(step && step.complete)
  const resolve = STEP_STATUS[step && step.key]
  if (!resolve) return complete ? statusOf('Done', 'done') : statusOf('Needs review', 'todo')
  return resolve(complete, stepData(step))
}

function areaCode(value) {
  const trimmed = String(value === null || value === undefined ? '' : value).trim()
  return trimmed || null
}

// Only a merchant who asked for an area code can have missed the one they asked
// for. Provisioning also records a substitution when nothing was requested, and
// telling that merchant they got the wrong number would be a lie.
export function areaCodeMismatch(storeDetails) {
  const data = storeDetails || {}
  if (data.area_code_matched !== false) return null
  const requested = areaCode(data.requested_area_code)
  if (!requested) return null
  return { requested, provisioned: areaCode(data.provisioned_area_code) }
}

export function shouldShowAreaCodeMismatch(storeDetails) {
  return areaCodeMismatch(storeDetails) !== null
}

// outstanding_count and complete are recomputed rather than carried over: a
// merged step leaves the server's counts a step behind, and anything reading
// them off the raw object would disagree with the list it is rendering.
export function mergeSetupStep(state, step) {
  if (!isSetupApplicable(state) || !step || !setupStepKeys().includes(step.key)) return state
  const steps = setupSteps(state).map(existing =>
    existing.key === step.key ? normalizeStep(step.key, step) : existing
  )
  const outstanding = steps.filter(candidate => !candidate.complete).length
  return { ...state, steps, outstanding_count: outstanding, complete: outstanding === 0 }
}

// The merge replaces the step whole, so the reason has to travel with the status
// it belongs to or a re-check that changes nothing wipes it off the screen. Only
// the state that can carry one gets the field: a reason left on a connected
// account would send the merchant back to fix something Stripe is happy with.
export function paymentsStepFromStatus(status, reason = null) {
  const resolved = status || STRIPE_NOT_CONNECTED
  const data =
    resolved === STRIPE_ACTION_REQUIRED
      ? { status: resolved, action_required_reason: reason || null }
      : { status: resolved }
  return { key: PAYMENTS_STEP, complete: resolved === STRIPE_CONNECTED, data }
}

// Resolves null rather than throwing. api.call turns 401 and 403 into navigation
// and commits every response it is not told to skip into the vuex object cache,
// so this asks for neither: a background refresh must not move the merchant off
// the page, and setup state is not a JSON:API object.
export async function fetchSetupState(apiCall) {
  try {
    const res = await withTimeout(
      apiCall('GET', SETUP_STATE_ENDPOINT, {}, { receive: false, redirectOnForbidden: false }),
      SETUP_REQUEST_TIMEOUT_MS,
      SETUP_STATE_ENDPOINT
    )
    const data = res && res.data
    return data && typeof data === 'object' && !Array.isArray(data) ? data : null
  } catch {
    return null
  }
}

const shared = ref(null)
const loading = ref(false)
const loaded = ref(false)
let inFlight = null
let generation = 0

// The generation bump is what makes this a real reset: a request already on the
// wire would otherwise land afterwards and refill the state it just cleared.
export function resetSetupState() {
  generation += 1
  inFlight = null
  shared.value = null
  loading.value = false
  loaded.value = false
  lastReturnRefreshAt = 0
}

export const SETUP_RETURN_REFRESH_GAP_MS = 3000

let returnApiCall = null
let returnBound = false
let lastReturnRefreshAt = 0

// Stripe finishes verifying in its own tab, an import finishes on a worker, an
// inbound message arrives - none of it tells the page anything, and a merchant
// should not have to reload to see their own setup move. Coming back to the tab
// is the one moment we reliably get.
//
// It lives here rather than on the step that needs it because a step is only
// mounted while its panel is open: collapsing it, or closing the drawer, took
// the re-check away with it. The shared state means one listener serves every
// reader, and refreshSetupState already folds concurrent callers into one
// request.
export function refreshSetupStateOnReturn() {
  if (typeof document === 'undefined' || document.visibilityState !== 'visible') return null
  if (!returnApiCall || !shared.value) return null
  if (!isSetupApplicable(shared.value) || isSetupComplete(shared.value)) return null
  // Flicking between tabs is one gesture, not a reason to ask again per switch.
  if (Date.now() - lastReturnRefreshAt < SETUP_RETURN_REFRESH_GAP_MS) return null

  lastReturnRefreshAt = Date.now()
  return refreshSetupState(returnApiCall)
}

function bindReturnRefresh(apiCall) {
  returnApiCall = apiCall
  if (returnBound || typeof document === 'undefined') return

  document.addEventListener('visibilitychange', refreshSetupStateOnReturn)
  returnBound = true
}

// One request however many readers ask at once: the sidebar badge and the drawer
// mount together, and api.call has no request dedupe of its own.
export function refreshSetupState(apiCall) {
  if (inFlight) return inFlight
  const startedAt = generation
  loading.value = true
  const request = fetchSetupState(apiCall)
    .then(next => {
      if (startedAt !== generation) return null
      // A failed refresh leaves what the merchant is already looking at in place
      // rather than blanking the drawer mid-edit.
      if (next) shared.value = next
      loaded.value = true
      return shared.value
    })
    .finally(() => {
      if (startedAt !== generation) return
      loading.value = false
      inFlight = null
    })
  inFlight = request
  return request
}

// Keyed on having state rather than on having tried: a first fetch lost to a
// blip would otherwise leave an applicable merchant with no drawer until they
// reload the dashboard.
export function ensureSetupState(apiCall) {
  if (shared.value) return Promise.resolve(shared.value)
  return refreshSetupState(apiCall)
}

export function applySetupStep(step) {
  shared.value = mergeSetupStep(shared.value, step)
  return shared.value
}

export function useSetupState(apiCall) {
  bindReturnRefresh(apiCall)
  return {
    setupState: shared,
    loading,
    loaded,
    applicable: computed(() => isSetupApplicable(shared.value)),
    visible: computed(() => isSetupVisible(shared.value)),
    complete: computed(() => isSetupComplete(shared.value)),
    pendingReview: computed(() => isSetupPendingReview(shared.value)),
    reviewed: computed(() => isSetupReviewed(shared.value)),
    outstandingCount: computed(() => outstandingStepCount(shared.value)),
    steps: computed(() => setupSteps(shared.value)),
    progress: computed(() => setupProgress(shared.value)),
    platform: computed(() => setupPlatform(shared.value)),
    openStepKey: computed(() => firstIncompleteStepKey(shared.value)),
    refresh: () => refreshSetupState(apiCall),
    ensure: () => ensureSetupState(apiCall),
    applyStep: applySetupStep
  }
}
